import React, { useState, useEffect, useMemo } from "react";
import { Select, App, Space } from "antd";
import { ShopOutlined } from "@ant-design/icons";
import { businessPartnerApi } from "../../api/contractManagementApi";

const { Option } = Select;

// Module-level cache
let cachedSuppliers = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

const fetchSupplierData = async (messageApi) => {
    const now = Date.now();
    if (cachedSuppliers && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
        return cachedSuppliers;
    }

    try {
        // 服务端按可选供应商口径返回（潜在或合作中），暂停的不会出现；不用再按启用筛
        const response = await businessPartnerApi.getSuppliers({ page_size: 500 });
        const supplierList = response?.data?.items || response?.data || [];
        if (supplierList.length > 0) {
            cachedSuppliers = supplierList;
            cacheTimestamp = now;
            return supplierList;
        }
        return [];
    } catch (error) {
        console.error("获取供应商列表失败:", error);
        if (messageApi) {
            messageApi.error("获取供应商列表失败");
        }
        // Return old cache if available, otherwise empty array
        return cachedSuppliers || [];
    }
};

/**
 * 可复用的供应商选择器组件
 * @param {object} props - 组件属性，透传给 antd Select 组件
 * @param {number | number[]} props.value - 当前选中的供应商ID（单选）或ID数组（多选）
 * @param {function} props.onChange - 选中项变化时的回调函数
 * @param {function} [props.renderLabel] - 自定义渲染标签的函数 (supplier) => ReactNode
 */
const SupplierSelector = ({ renderLabel, onChange, ...restProps }) => {
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const { message: messageApi } = App.useApp();

    useEffect(() => {
        let isMounted = true;
        const loadSuppliers = async () => {
            setLoading(true);
            const data = await fetchSupplierData(messageApi);
            if (isMounted) {
                setSupplierOptions(data);
                setLoading(false);
            }
        };
        loadSuppliers();
        return () => {
            isMounted = false;
        };
    }, [messageApi]);

    // 默认标签渲染
    const defaultRenderLabel = (supplier) => {
        return (
            <Space>
                <ShopOutlined style={{ color: "#52c41a" }} />
                <span>{supplier.name}</span>
            </Space>
        );
    };

    const options = useMemo(() => {
        return supplierOptions.map((supplier) => ({
            value: supplier.id,
            label: renderLabel ? renderLabel(supplier) : defaultRenderLabel(supplier),
            // 保存原始数据用于筛选
            supplier: supplier,
            // For filtering
            searchValue: `${supplier.name}`.toLowerCase(),
        }));
    }, [supplierOptions, renderLabel]);

    const filterOption = (input, option) => {
        return option?.searchValue?.includes(input.toLowerCase());
    };

    // 处理选择变更，确保传递option参数
    const handleChange = (value, option) => {
        if (onChange) {
            onChange(value, option);
        }
    };

    return <Select showSearch placeholder="选择或搜索供应商" loading={loading} options={options} optionFilterProp="searchValue" filterOption={filterOption} onChange={handleChange} {...restProps} style={{ ...restProps.style, margin: 0 }} />;
};

export default SupplierSelector;
